import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Calendar, ArrowLeft, Loader, CheckCircle, XCircle } from 'lucide-react';
import { dbPromise } from '../lib/db';
import WaveAnimation from '../components/WaveAnimation';

interface PaymentRequest {
  id: number;
  name: string;
  email: string;
  cardNumber: string;
  nameOnCard: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export default function AdminPayments() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<PaymentRequest[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAdmin = () => {
      const user = localStorage.getItem('user');
      if (!user) {
        navigate('/login');
        return false;
      }
      
      const userData = JSON.parse(user);
      if (userData.email !== 'notrakibs@gmail.com') {
        navigate('/');
        return false;
      }
      
      return true;
    };

    const loadPayments = async () => {
      if (!checkAdmin()) return;

      try {
        const db = await dbPromise;
        const tx = db.transaction('payment_requests', 'readonly');
        const store = tx.objectStore('payment_requests');
        const payments = await store.getAll();
        
        // Sort payments by date, newest first
        payments.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        
        setPayments(payments);
      } catch (error) {
        console.error('Failed to load payments:', error);
        setError('Failed to load payment requests. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadPayments();
  }, [navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCardNumber = (cardNumber: string) => {
    return `•••• ${cardNumber.slice(-4)}`;
  };

  const getStatusColor = (status: PaymentRequest['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300';
      case 'approved':
        return 'bg-green-500/20 text-green-300';
      case 'rejected':
        return 'bg-red-500/20 text-red-300';
    }
  };

  const handleStatusChange = async (paymentId: number, newStatus: PaymentRequest['status']) => {
    try {
      const db = await dbPromise;
      const tx = db.transaction('payment_requests', 'readwrite');
      const store = tx.objectStore('payment_requests');
      
      const payment = await store.get(paymentId);
      if (payment) {
        payment.status = newStatus;
        await store.put(payment);
        
        // Update local state
        setPayments(prev => 
          prev.map(p => 
            p.id === paymentId ? { ...p, status: newStatus } : p
          )
        );
      }
      
      await tx.done;
    } catch (error) {
      console.error('Failed to update payment status:', error);
      setError('Failed to update payment status. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 pt-24 flex items-center justify-center">
        <Loader className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pt-24 relative overflow-hidden">
      <WaveAnimation />
      
      <div className="container mx-auto px-4 py-12 relative z-10">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-8">Payment Requests</h1>

          {error ? (
            <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 text-red-500">
              {error}
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No payment requests yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {payments.map((payment) => (
                <div 
                  key={payment.id}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-grow space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-4 mb-2">
                            <h3 className="text-xl font-semibold text-white">
                              {payment.name}
                            </h3>
                            <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(payment.status)}`}>
                              {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-gray-400">
                            <span>{payment.email}</span>
                            <span>•</span>
                            <span>BDT {payment.amount.toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <Calendar className="w-4 h-4" />
                          {formatDate(payment.createdAt)}
                        </div>
                      </div>

                      <div className="flex items-center gap-8">
                        <div className="flex items-center gap-2 text-gray-400">
                          <CreditCard className="w-4 h-4" />
                          <span>{formatCardNumber(payment.cardNumber)}</span>
                        </div>
                        <div className="text-gray-400">
                          Name on Card: {payment.nameOnCard}
                        </div>
                      </div>

                      {payment.status === 'pending' && (
                        <div className="flex items-center gap-4 pt-4">
                          <button
                            onClick={() => handleStatusChange(payment.id, 'approved')}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleStatusChange(payment.id, 'rejected')}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}